/* ILVA button label roll-up (Framer-style) — progressive enhancement.

   Wraps a button's text label in two stacked copies so the label rolls up on
   hover (the colour inversion is handled by ilva-buttons.css). Built at runtime,
   reversible, and applied SITE-WIDE to every plain-text ILVA button:
     - .btn--primary / .btn--secondary / .btn--custom whose content is plain text
       (checkout, add-to-cart, "view details", view-cart, login, content CTAs, …)
     - the campaign-feature composite button's label span (.campaign-feature__btn-text)

   FUNCTIONALITY IS NEVER AFFECTED. Only the inner label text is wrapped, so a
   button's name / type / value / href / form / event listeners are all untouched.
   The wrapper is click-through (`pointer-events: none` on .btn-roll in
   ilva-buttons.css), so a click on the label still has the BUTTON as
   `evt.target` — required by the delegated handlers that test the exact target
   (e.g. Modal.handleClick's `evt.target.matches('.js-close-modal')`), which
   bubbling alone would not satisfy. Add-to-cart still adds, checkout still
   submits, etc. Verified against a fractional-line-height harness and the real
   add-to-cart / checkout / variant-change flows.

   What is NEVER wrapped:
     - Any button containing child ELEMENTS (icons, spinners, composite layouts):
       quick-add cards, slider arrows, icon buttons — skipped automatically.
     - Shopify-rendered payment / dynamic-checkout buttons (their markup is owned
       by Shopify) — excluded by ancestor.

   SELF-HEALING. Some labels are rewritten by the theme's own JS — e.g.
   variant-picker.js sets the add-to-cart button's textContent on every variant
   change (sold-out / select-options / add-to-cart), which would wipe the roll. A
   per-element MutationObserver re-wraps the CURRENT label whenever that happens,
   so the theme's JS stays completely untouched and the label always reflects the
   live state, rolled. Our own re-wrap leaves a .btn-roll child, which the
   observer ignores — so there is no feedback loop.

   Hover-capable, non-reduced-motion devices only: on touch / reduced-motion
   nothing is wrapped at all, so that markup (and behaviour) is left pristine. */
(function () {
  if (window.__ilvaBtnRoll) return;
  window.__ilvaBtnRoll = {};

  try {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  } catch (e) {
    return;
  }

  // Plain-text buttons + the campaign-feature label span.
  var TARGETS = '.btn--primary, .btn--secondary, .btn--custom, .campaign-feature__btn-text';

  // Never wrap: Shopify payment buttons, carousel arrows, icon buttons, quick-add
  // view details, and any button that swaps its OWN label via JS. The roll splits
  // the label into two copies, so the button's `.textContent` reads back DOUBLED —
  // fine for code that only WRITES the label (the observer re-heals), but it breaks
  // code that READS it. [data-toggle-text] marks self-toggling buttons (e.g. the
  // product-comparison "show all rows" toggle) whose JS reads textContent; exclude
  // them. (Future toggle buttons that read their own text should use this attribute
  // or be added here.)
  var DENY = '.shopify-payment-button__button, .slider-nav__btn, .btn--icon, .quick-add-view-btn, [data-toggle-text]';

  // Never wrap anything inside a Shopify-owned payment / dynamic-checkout area.
  var DENY_ANCESTOR =
    '.shopify-payment-button, .dynamic-cart-btns, ' +
    '.product-info__dynamic-checkout-button, .additional-checkout-buttons';

  // Build the two-copy roll from the element's current text. Returns false if
  // there's nothing to wrap.
  function wrap(el) {
    var label = el.textContent;
    if (!label || !label.trim()) return false;

    var roll = document.createElement('span');
    roll.className = 'btn-roll';

    var a = document.createElement('span');
    a.className = 'btn-roll__line btn-roll__line--a';
    a.textContent = label;

    var b = document.createElement('span');
    b.className = 'btn-roll__line btn-roll__line--b';
    b.setAttribute('aria-hidden', 'true'); // duplicate must not be read out twice
    b.textContent = label;

    roll.appendChild(a);
    roll.appendChild(b);
    el.textContent = '';
    el.appendChild(roll);
    return true;
  }

  // Re-wrap if the theme's JS resets the label to a plain text node. Attached
  // AFTER the initial wrap, so it never sees our own wrap; it only fires when the
  // .btn-roll has been replaced by plain text.
  function watch(el) {
    if (el.__btnRollMO) return;
    el.__btnRollMO = new MutationObserver(function () {
      if (el.querySelector('.btn-roll')) return; // our structure is intact → ignore
      if (el.textContent && el.textContent.trim()) wrap(el);
    });
    el.__btnRollMO.observe(el, { childList: true });
  }

  function enhance(el) {
    if (el.dataset.btnRoll) return;
    if (el.children.length > 0) return; // has child elements → composite/icon button, skip
    if (el.matches(DENY) || el.closest(DENY_ANCESTOR)) return;
    if (!el.textContent || !el.textContent.trim()) return;

    el.dataset.btnRoll = '1';
    if (wrap(el)) watch(el);
  }

  function scan(root) {
    (root || document).querySelectorAll(TARGETS).forEach(enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Theme editor: a re-rendered section brings fresh buttons.
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });

  // Expose scan for dynamically injected content (e.g. AJAX-loaded sections).
  window.__ilvaBtnRoll.scan = scan;
})();
